/**
 * Unified Authentication Context
 *
 * Single source of truth for authentication across the entire application.
 * Uses Neon Auth with BetterAuthReactAdapter for session management.
 *
 * Benefits:
 * - Decouples app from auth provider specifics
 * - Provides consistent auth interface
 * - Role-based access control via platformRole from database
 * - Native Neon integration for seamless database access
 */

import { createContext, useContext, ReactNode, useMemo, useEffect, useState, useCallback } from 'react'
import { authClient } from '@/lib/auth-client'

// Platform roles with hierarchy: admin > editor > viewer > user
export type PlatformRole = 'admin' | 'editor' | 'viewer' | 'user'

const ROLE_HIERARCHY: Record<PlatformRole, number> = {
  user: 0,
  viewer: 1,
  editor: 2,
  admin: 3,
}

// Unified user type
export interface AuthUser {
  id: string
  email: string | null
  displayName: string | null
  profileImageUrl: string | null
  platformRole: PlatformRole
}

// Auth context value
interface AuthContextValue {
  // User state
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean

  // Auth actions
  signIn: () => void
  signInWithGitHub: () => void
  signInWithGoogle: () => void
  signOut: () => Promise<void>

  // Role-based access control
  hasRole: (requiredRole: PlatformRole) => boolean
  isAdmin: boolean
  isEditor: boolean
  isViewer: boolean

  // Refresh profile data
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  // Neon Auth session hook
  const session = authClient.useSession()

  // Profile state from /api/me endpoint
  const [profileData, setProfileData] = useState<{
    platformRole: PlatformRole
  } | null>(null)
  const [profileLoading, setProfileLoading] = useState(false)

  // Fetch user profile from database
  const fetchProfile = useCallback(async () => {
    if (!session.data?.user) {
      setProfileData(null)
      return
    }

    setProfileLoading(true)
    try {
      const response = await fetch('/api/me', {
        credentials: 'include',
      })

      if (response.ok) {
        const data = await response.json() as {
          platformRole?: string
        }
        setProfileData({
          platformRole: (data.platformRole as PlatformRole) || 'user',
        })
      } else {
        // User authenticated but profile fetch failed - use default role
        setProfileData({
          platformRole: 'user',
        })
      }
    } catch (error) {
      console.error('[AuthContext] Failed to fetch profile:', error)
      setProfileData({
        platformRole: 'user',
      })
    } finally {
      setProfileLoading(false)
    }
  }, [session.data?.user])

  // Fetch profile when session changes
  useEffect(() => {
    if (session.data?.user) {
      fetchProfile()
    } else {
      setProfileData(null)
    }
  }, [session.data?.user, fetchProfile])

  // Build unified AuthUser from Neon Auth session + profile data
  const user: AuthUser | null = useMemo(() => {
    if (!session.data?.user) return null

    return {
      id: session.data.user.id,
      email: session.data.user.email ?? null,
      displayName: session.data.user.name ?? null,
      profileImageUrl: session.data.user.image ?? null,
      platformRole: profileData?.platformRole ?? 'user',
    }
  }, [session.data?.user, profileData])

  // Loading state: session loading OR profile loading
  const isLoading = session.isPending || (!!session.data?.user && profileLoading)

  // Authenticated state
  const isAuthenticated = !!session.data?.user

  // Sign in (default to GitHub)
  const signIn = useCallback(() => {
    authClient.signIn.social({
      provider: 'github',
      callbackURL: '/',
    })
  }, [])

  // Sign in with GitHub
  const signInWithGitHub = useCallback(() => {
    authClient.signIn.social({
      provider: 'github',
      callbackURL: '/',
    })
  }, [])

  // Sign in with Google
  const signInWithGoogle = useCallback(() => {
    authClient.signIn.social({
      provider: 'google',
      callbackURL: '/',
    })
  }, [])

  // Sign out
  const signOut = useCallback(async () => {
    await authClient.signOut()
    setProfileData(null)
  }, [])

  // Role checking using hierarchy
  const hasRole = useCallback((requiredRole: PlatformRole): boolean => {
    if (!user) return false
    const userLevel = ROLE_HIERARCHY[user.platformRole] ?? 0
    const requiredLevel = ROLE_HIERARCHY[requiredRole] ?? 0
    return userLevel >= requiredLevel
  }, [user])

  // Convenience role checks
  const isAdmin = useMemo(() => hasRole('admin'), [hasRole])
  const isEditor = useMemo(() => hasRole('editor'), [hasRole])
  const isViewer = useMemo(() => hasRole('viewer'), [hasRole])

  // Refresh profile data
  const refreshProfile = useCallback(async () => {
    await fetchProfile()
  }, [fetchProfile])

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated,
    signIn,
    signInWithGitHub,
    signInWithGoogle,
    signOut,
    hasRole,
    isAdmin,
    isEditor,
    isViewer,
    refreshProfile,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Hook to access unified authentication state
 */
export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }
  return context
}

// Re-export as useAuth for backward compatibility
export { useAuthContext as useAuth }

/**
 * Hook to require authentication (throws if not authenticated)
 */
export function useRequireAuth(): Omit<AuthContextValue, 'user'> & { user: AuthUser } {
  const auth = useAuthContext()
  if (!auth.isAuthenticated || !auth.user) {
    throw new Error('useRequireAuth called but user is not authenticated')
  }
  return { ...auth, user: auth.user }
}

/**
 * Hook to check if user is authenticated (for conditional rendering)
 */
export function useIsAuthenticated(): boolean {
  const { isAuthenticated } = useAuthContext()
  return isAuthenticated
}

/**
 * Hook to require a specific role (throws if not authorized)
 */
export function useRequireRole(
  requiredRole: PlatformRole
): Omit<AuthContextValue, 'user'> & { user: AuthUser } {
  const auth = useAuthContext()

  if (!auth.isAuthenticated || !auth.user) {
    throw new Error(`useRequireRole('${requiredRole}') called but user is not authenticated`)
  }

  if (!auth.hasRole(requiredRole)) {
    throw new Error(
      `useRequireRole('${requiredRole}') called but user has insufficient permissions (role: ${auth.user.platformRole})`
    )
  }

  return { ...auth, user: auth.user }
}
